// Pure date→position transforms for the Gantt timeline (STD-014 split out of
// SimpleGanttChart). No React: these functions take raw records + a year and
// return percentage offsets across that year's bar, so they can be unit-tested
// in isolation. All percentages are clamped to [0, 100].

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export interface TimelineMaintenanceRecord {
  id: string;
  start_date: string;
  end_date: string;
  maintenance_type: string;
  performed_by_name?: string;
}

export interface TimelineNote {
  id: string;
  date_of_action?: string;
  subject: string;
}

export interface FlightDatePosition {
  date: string;
  position: number;
}

export interface MaintenancePosition {
  record: TimelineMaintenanceRecord;
  startPosition: number;
  endPosition: number;
  width: number;
}

export interface NotePosition {
  note: TimelineNote;
  date: string;
  position: number;
}

// Jan 1 of the given year — the left edge (0%) of the timeline bar.
export function yearStartDate(year: number): Date {
  return new Date(year, 0, 1);
}

// Dec 31 of the given year — the right edge of the timeline bar.
export function yearEndDate(year: number): Date {
  return new Date(year, 11, 31);
}

// Total days spanned by the year (365, or 366 for leap years). Used as the
// denominator for every position calculation.
export function totalDaysInYear(year: number): number {
  return Math.ceil((yearEndDate(year).getTime() - yearStartDate(year).getTime()) / MS_PER_DAY);
}

// Clamp a raw percentage to the visible [0, 100] range of the bar.
function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

// Percentage offset of a single date from the start of the year.
function datePercent(date: Date, startDate: Date, totalDays: number): number {
  const daysFromStart = Math.ceil((date.getTime() - startDate.getTime()) / MS_PER_DAY);
  return (daysFromStart / totalDays) * 100;
}

// Collect every distinct year that appears across flights, maintenance records,
// and notes — sorted descending (latest first) so the UI defaults to the most
// recent year with data.
export function collectYears(
  flightDates: string[],
  maintenanceRecords: TimelineMaintenanceRecord[],
  notes: TimelineNote[]
): number[] {
  const years = new Set<number>();
  flightDates.forEach((d) => {
    const year = new Date(d).getFullYear();
    if (!isNaN(year)) years.add(year);
  });
  maintenanceRecords.forEach((r) => {
    const startYear = new Date(r.start_date).getFullYear();
    const endYear = new Date(r.end_date).getFullYear();
    if (!isNaN(startYear)) years.add(startYear);
    if (!isNaN(endYear)) years.add(endYear);
  });
  notes.forEach((n) => {
    if (n.date_of_action) {
      const year = new Date(n.date_of_action).getFullYear();
      if (!isNaN(year)) years.add(year);
    }
  });
  return Array.from(years).sort((a, b) => b - a);
}

// Flight tick positions for a single year.
export function flightPositions(flightDates: string[], year: number): FlightDatePosition[] {
  const startDate = yearStartDate(year);
  const totalDays = totalDaysInYear(year);
  return flightDates
    .filter((dateStr) => new Date(dateStr).getFullYear() === year)
    .map((dateStr) => ({
      date: dateStr,
      position: clampPercent(datePercent(new Date(dateStr), startDate, totalDays)),
    }));
}

// Maintenance bar positions for a single year. A record is included when any
// part of its range overlaps the year; the bar has a minimum width for
// visibility.
export function maintenancePositions(
  records: TimelineMaintenanceRecord[],
  year: number
): MaintenancePosition[] {
  const startDate = yearStartDate(year);
  const totalDays = totalDaysInYear(year);
  return records
    .filter((record) => {
      const recordStart = new Date(record.start_date);
      const recordEnd = new Date(record.end_date);
      return recordStart.getFullYear() <= year && recordEnd.getFullYear() >= year;
    })
    .map((record) => {
      const startPercentage = clampPercent(
        datePercent(new Date(record.start_date), startDate, totalDays)
      );
      const endPercentage = clampPercent(
        datePercent(new Date(record.end_date), startDate, totalDays)
      );
      const width = Math.max(0.5, endPercentage - startPercentage);
      return { record, startPosition: startPercentage, endPosition: endPercentage, width };
    });
}

// Note marker positions for a single year. Notes without a `date_of_action` are
// dropped.
export function notePositions(notes: TimelineNote[], year: number): NotePosition[] {
  const startDate = yearStartDate(year);
  const totalDays = totalDaysInYear(year);
  return notes
    .filter((note) => {
      if (!note.date_of_action) return false;
      return new Date(note.date_of_action).getFullYear() === year;
    })
    .map((note) => ({
      note,
      date: note.date_of_action!,
      position: clampPercent(datePercent(new Date(note.date_of_action!), startDate, totalDays)),
    }));
}
